import { getPathAlias } from 'tw-oi-core/utils/vehicle'

import { BRANDS } from '../config'
import { hydrateVehicleContents } from '../services/offlineContentHydrator'
import {
  getDownloadedCategories,
  getOfflineDownloadState,
  getVehicleContents,
  hasAnyOfflineContent,
} from '../services/offlineStorage'
import { findOfflineVehicleRecord, getOfflineVehicleRecords } from './offlineVehicles'

/**
 * Resolves brand route prefix from a guide base route
 *
 * @param {string} baseRoute guide base route e.g. /dynamaxrv/guide/2019/foo
 * @returns {string} brand route prefix e.g. /dynamaxrv
 */
export function resolveBrandRouteFromBaseRoute(baseRoute) {
  const match = baseRoute && baseRoute.match(/^\/([^/]+)/)
  return match ? `/${match[1]}` : '/'
}

/**
 * Parses year and model slug from a guide base route
 *
 * @param {string} baseRoute guide route without browse suffix
 * @returns {{ year: string, model: string }|null}
 */
export function parseGuideRoute(baseRoute) {
  const match = baseRoute && baseRoute.match(/\/guide\/([^/]+)\/([^/]+)$/)
  if (!match) return null

  return { year: match[1], model: match[2] }
}

/**
 * Resolves brand name from the URL brand route segment
 *
 * @param {string} brandRoute brand route prefix e.g. /forest-river
 * @param {string} currentBrand fallback brand from Redux
 * @returns {string}
 */
export function resolveBrandFromRoute(brandRoute, currentBrand) {
  const brandKey = (brandRoute || '/').replace(/^\/+/, '')
  return BRANDS.find(item => item.key === brandKey)?.name || currentBrand
}

/**
 * Finds offline vehicle metadata for a guide route
 *
 * @param {object} options loader options
 * @param {string} options.baseRoute guide base route
 * @param {string} options.brandRoute brand route prefix
 * @param {string} options.currentBrand active brand name
 * @param {string} [options.year] model year override
 * @param {string} [options.model] model slug override
 * @returns {Promise<object|null>} offline record or null
 */
export async function findOfflineVehicleForRoute({
  baseRoute,
  brandRoute,
  currentBrand,
  year,
  model,
}) {
  const parsed = parseGuideRoute(baseRoute)
  const routeYear = year || parsed?.year
  const routeModel = model || parsed?.model

  if (!routeYear || !routeModel) return null

  const brand = resolveBrandFromRoute(brandRoute, currentBrand)
  const records = await getOfflineVehicleRecords()

  return findOfflineVehicleRecord(records, brand, routeYear, routeModel, getPathAlias)
}

/**
 * Writes offline vehicle metadata into Redux
 *
 * @param {object} vehicleActions vehicle action creators
 * @param {object} vehicle offline vehicle info
 * @param {object} currentVehicle current Redux vehicle slice
 */
export function syncOfflineVehicleToStore(vehicleActions, vehicle, currentVehicle) {
  const {
    setVehicleBrand,
    setVehicleModel,
    setVehicleId,
    setVehicleYear,
    setVehicleProductType,
    setVehicleProductSubtype,
  } = vehicleActions

  if (vehicle.make !== currentVehicle.currentBrand) setVehicleBrand(vehicle.make)
  if (String(vehicle.year) !== String(currentVehicle.currentYear)) setVehicleYear(String(vehicle.year))
  if (vehicle.model !== currentVehicle.currentModel) setVehicleModel(vehicle.model)
  if (String(vehicle.id) !== String(currentVehicle.currentId)) setVehicleId(vehicle.id)
  if (vehicle.FOREST_RIVER_PRODUCT_TYPE !== currentVehicle.currentProductType) {
    setVehicleProductType(vehicle.FOREST_RIVER_PRODUCT_TYPE)
  }
  if (vehicle.FOREST_RIVER_PRODUCT_SUB_TYPE !== currentVehicle.currentProductSubtype) {
    setVehicleProductSubtype(vehicle.FOREST_RIVER_PRODUCT_SUB_TYPE)
  }
}

/**
 * Returns whether Redux vehicle state already matches offline vehicle metadata
 *
 * @param {object} vehicleInfo offline vehicle metadata
 * @param {object} currentVehicle Redux vehicle slice
 * @returns {boolean}
 */
export function isVehicleStateSynced(vehicleInfo, currentVehicle) {
  return (
    vehicleInfo.make === currentVehicle.currentBrand &&
    String(vehicleInfo.year) === String(currentVehicle.currentYear) &&
    vehicleInfo.model === currentVehicle.currentModel &&
    String(vehicleInfo.id) === String(currentVehicle.currentId) &&
    vehicleInfo.FOREST_RIVER_PRODUCT_TYPE === currentVehicle.currentProductType &&
    vehicleInfo.FOREST_RIVER_PRODUCT_SUB_TYPE === currentVehicle.currentProductSubtype
  )
}

/**
 * Returns whether Redux already has hydrated content for a vehicle
 *
 * @param {object} contents Redux contents slice
 * @param {object} vehicle Redux vehicle slice
 * @param {string} vehicleId offline vehicle id
 * @param {object} vehicleInfo offline vehicle metadata
 * @returns {boolean}
 */
export function hasHydratedContentsForVehicle(contents, vehicle, vehicleId, vehicleInfo) {
  return !!(
    contents.publications &&
    String(vehicle.currentId) === String(vehicleId) &&
    String(vehicle.currentYear) === String(vehicleInfo.year) &&
    vehicle.currentModel === vehicleInfo.model
  )
}

/**
 * Loads vehicle content from offline storage into Redux
 *
 * @param {object} options loader options
 * @param {Function} options.dispatch Redux dispatch
 * @param {object} options.vehicleActions vehicle action creators
 * @param {object} options.vehicle Redux vehicle slice
 * @param {object} options.contents Redux contents slice
 * @param {string} options.baseRoute guide base route
 * @param {string} options.brandRoute brand route prefix
 * @param {string} [options.year] model year override
 * @param {string} [options.model] model slug override
 * @returns {Promise<object|null>} load result or null when unavailable
 */
export async function loadOfflineGuideVehicle({
  dispatch,
  vehicleActions,
  vehicle,
  contents,
  baseRoute,
  brandRoute,
  year,
  model,
}) {
  const hasContent = await hasAnyOfflineContent()
  if (!hasContent) {
    return { ok: false, empty: true }
  }

  const record = await findOfflineVehicleForRoute({
    baseRoute,
    brandRoute,
    currentBrand: vehicle.currentBrand,
    year,
    model,
  })

  if (!record || !record.vehicleInfo) {
    return { ok: false, empty: false }
  }

  const vehicleId = String(record.vehicleId)
  const vehicleInfo = record.vehicleInfo

  const alreadyHydrated = hasHydratedContentsForVehicle(
    contents,
    { currentId: vehicleId, currentYear: vehicleInfo.year, currentModel: vehicleInfo.model },
    vehicleId,
    vehicleInfo
  )

  let snapshot = null

  if (!alreadyHydrated) {
    const result = await hydrateVehicleContents(dispatch, vehicleId)
    if (!result) {
      return { ok: false, empty: false }
    }
    snapshot = result.snapshot
  } else {
    snapshot = await getVehicleContents(vehicleId)
  }

  if (!isVehicleStateSynced(vehicleInfo, vehicle)) {
    syncOfflineVehicleToStore(vehicleActions, vehicleInfo, vehicle)
  }

  const [downloadedCategories, downloadState] = await Promise.all([
    getDownloadedCategories(vehicleId),
    getOfflineDownloadState(vehicleId),
  ])

  return {
    ok: true,
    vehicleId,
    vehicleInfo,
    snapshot,
    downloadedCategories,
    downloadState,
    ownersManualPdfs: snapshot?.ownersManualPdfs || [],
  }
}
